import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAuthToken } from '../../utils/authUtils';
import {
  setBills,
  setCurrentBill,
  setLoading,
  setError,
  updateBill,
  addBill,
  emailBillStart,
  emailBillSuccess,
  emailBillFailure,
  // Month End Close actions
  setBillingPeriods,
  setCurrentPeriod,
  updatePeriod,
  setBillingConfig,
  setPeriodsLoading,
  setPeriodsError,
  setPeriodValidation,
  clearPeriodValidation
} from '../slices/billingSlice';
import {
  getAllPeriods as fetchAllPeriods,
  getCurrentPeriod as fetchCurrentPeriod,
  getPeriodStatus as fetchPeriodStatus,
  initiateSoftClose as apiSoftClose,
  initiateHardClose as apiHardClose,
  reopenPeriod as apiReopenPeriod,
  validateTransactionDate as apiValidateDate,
  refreshPeriodSummary as apiRefreshSummary,
  getBillingConfig as apiFetchConfig,
  updateBillingConfig as apiUpdateConfig
} from '../../services/monthEndCloseService';

// Helper function to validate date values
function isValidDate(dateValue) {
  if (!dateValue) return false;
  try {
    const date = new Date(dateValue);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}


// Helper function to get API base URL
function getApiBaseUrl() {
  return process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
}

// Fetch all bills
export const fetchBills = (userData) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true));
    
    if (!userData) {
      dispatch(setError('User data is required'));
      return;
    }
    
    // Build query parameters
    const params = new URLSearchParams();
    if (userData.facilityId) {
      params.append('facilityId', userData.facilityId);
    }
    params.append('page', '1');
    params.append('limit', '1000'); // Get all bills for now
    
    // Add date range filter (default to 6 months handled by server)
    if (userData.startDate) {
      params.append('startDate', userData.startDate);
    }
    if (userData.endDate) {
      params.append('endDate', userData.endDate);
    }
    
    const token = await getAuthToken();
    const response = await fetch(
      `${getApiBaseUrl()}/billing/bills?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch bills');
    }

    const result = await response.json();
    
    // Convert any Timestamp objects to ISO strings with validation
    const bills = result.bills.map(bill => ({
      ...bill,
      createdAt: bill.createdAt?.toDate?.() ? bill.createdAt.toDate().toISOString() : 
                 (bill.createdAt && isValidDate(bill.createdAt) ? bill.createdAt : null),
      updatedAt: bill.updatedAt?.toDate?.() ? bill.updatedAt.toDate().toISOString() : 
                 (bill.updatedAt && isValidDate(bill.updatedAt) ? bill.updatedAt : null),
      dueDate: bill.dueDate?.toDate?.() ? bill.dueDate.toDate().toISOString() : 
               (bill.dueDate && isValidDate(bill.dueDate) ? bill.dueDate : null),
      lastPaymentDate: bill.lastPaymentDate?.toDate?.() ? bill.lastPaymentDate.toDate().toISOString() : 
                      (bill.lastPaymentDate && isValidDate(bill.lastPaymentDate) ? bill.lastPaymentDate : null),
      payments: bill.payments?.map(payment => ({
        ...payment,
        date: payment.date?.toDate?.() ? payment.date.toDate().toISOString() : 
              (payment.date && isValidDate(payment.date) ? payment.date : null)
      }))
    }));
    
    dispatch(setBills(bills));
  } catch (error) {
    console.error('Error fetching bills:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

// Fetch bills by patient ID
export const fetchPatientBills = (patientId, userData) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true));
    
    if (!userData) {
      dispatch(setError('User data is required'));
      return;
    }
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append('patientId', patientId);
    if (userData.facilityId) {
      params.append('facilityId', userData.facilityId);
    }
    params.append('page', '1');
    params.append('limit', '1000');
    
    const token = await getAuthToken();
    const response = await fetch(
      `${getApiBaseUrl()}/billing/bills?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch patient bills');
    }

    const result = await response.json();
    
    // Convert any Timestamp objects to ISO strings
    const bills = result.bills.map(bill => ({
      ...bill,
      createdAt: bill.createdAt?.toDate?.() ? bill.createdAt.toDate().toISOString() : bill.createdAt,
      updatedAt: bill.updatedAt?.toDate?.() ? bill.updatedAt.toDate().toISOString() : bill.updatedAt,
      dueDate: bill.dueDate?.toDate?.() ? bill.dueDate.toDate().toISOString() : bill.dueDate,
      lastPaymentDate: bill.lastPaymentDate?.toDate?.() ? bill.lastPaymentDate.toDate().toISOString() : bill.lastPaymentDate,
      payments: bill.payments?.map(payment => ({
        ...payment,
        date: payment.date?.toDate?.() ? payment.date.toDate().toISOString() : payment.date
      }))
    }));
    
    dispatch(setBills(bills));
  } catch (error) {
    console.error('Error fetching patient bills:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

// Fetch single bill
export const fetchBillById = (billId, userData) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true));
    
    if (!userData) {
      dispatch(setError('User data is required'));
      return;
    }
    
    const token = await getAuthToken();
    const response = await fetch(
      `${getApiBaseUrl()}/billing/bills/${billId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        dispatch(setError('Bill not found'));
        return;
      }
      throw new Error('Failed to fetch bill');
    }

    const result = await response.json();
    const billData = result.bill;
    
    // Convert any Timestamp objects to ISO strings with validation
    const serializedBill = {
      ...billData,
      createdAt: billData.createdAt?.toDate?.() ? billData.createdAt.toDate().toISOString() : 
                 (billData.createdAt && isValidDate(billData.createdAt) ? billData.createdAt : null),
      updatedAt: billData.updatedAt?.toDate?.() ? billData.updatedAt.toDate().toISOString() : 
                 (billData.updatedAt && isValidDate(billData.updatedAt) ? billData.updatedAt : null),
      dueDate: billData.dueDate?.toDate?.() ? billData.dueDate.toDate().toISOString() : 
               (billData.dueDate && isValidDate(billData.dueDate) ? billData.dueDate : null),
      lastPaymentDate: billData.lastPaymentDate?.toDate?.() ? billData.lastPaymentDate.toDate().toISOString() : 
                      (billData.lastPaymentDate && isValidDate(billData.lastPaymentDate) ? billData.lastPaymentDate : null),
      payments: billData.payments?.map(payment => ({
        ...payment,
        date: payment.date?.toDate?.() ? payment.date.toDate().toISOString() : 
              (payment.date && isValidDate(payment.date) ? payment.date : null)
      }))
    };
    
    dispatch(setCurrentBill({
      id: billId,
      ...serializedBill
    }));
  } catch (error) {
    console.error('Error fetching bill:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

// Process payment
export const processPayment = (billId, paymentData, userData) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true));
    
    if (!userData) {
      dispatch(setError('User data is required'));
      return;
    }
    
    const token = await getAuthToken();
    const response = await fetch(
      `${getApiBaseUrl()}/billing/bills/${billId}/payment`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      }
    );

    if (!response.ok) {
      throw new Error('Failed to process payment');
    }

    const result = await response.json();
    
    // Update the bill in Redux state
    dispatch(updateBill({
      id: billId,
      status: result.newStatus,
      paidAmount: result.totalPaid,
      lastPaymentDate: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }));
    
    return result;
  } catch (error) {
    console.error('Error processing payment:', error);
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Approve bill - changes status to 'approved' and triggers claim processing
export const approveBill = (billId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    
    const token = await getAuthToken();
    const response = await fetch(
      `${getApiBaseUrl()}/billing/bills/${billId}/status`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'approved' })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to approve bill');
    }

    const result = await response.json();
    
    // Update the bill in Redux state
    dispatch(updateBill({
      id: billId,
      status: 'approved',
      updatedAt: new Date().toISOString()
    }));
    
    return result;
  } catch (error) {
    console.error('Error approving bill:', error);
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Create bill
export const createBill = (billData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    
    const token = await getAuthToken();
    const response = await fetch(
      `${getApiBaseUrl()}/billing/bills/create`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billData)
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create bill');
    }

    const result = await response.json();
    dispatch(addBill(result.bill));
    return result;
  } catch (error) {
    console.error('Error creating bill:', error);
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

export const emailBill = createAsyncThunk(
  'billing/emailBill',
  async (billId, { dispatch }) => {
    try {
      dispatch(emailBillStart());
      
      const token = await getAuthToken();
      const response = await fetch(
        `${getApiBaseUrl()}/billing/bills/${billId}/email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send bill email');
      }

      const result = await response.json();
      dispatch(emailBillSuccess());
      return result;
    } catch (error) {
      console.error('Error emailing bill:', error);
      dispatch(emailBillFailure(error.message));
      throw error;
    }
  }
);

// ============================================
// Month End Close Thunks
// ============================================

// Fetch all billing periods for a facility
export const fetchBillingPeriods = (facilityId, limit = 24) => async (dispatch) => {
  try {
    dispatch(setPeriodsLoading(true));
    const periods = await fetchAllPeriods(facilityId, limit);
    dispatch(setBillingPeriods(periods));
  } catch (error) {
    console.error('Error fetching billing periods:', error);
    dispatch(setPeriodsError(error.message));
  } finally {
    dispatch(setPeriodsLoading(false));
  }
};

// Fetch current billing period
export const fetchCurrentBillingPeriod = (facilityId) => async (dispatch) => {
  try {
    dispatch(setPeriodsLoading(true));
    const period = await fetchCurrentPeriod(facilityId);
    dispatch(setCurrentPeriod(period));
  } catch (error) {
    console.error('Error fetching current period:', error);
    dispatch(setPeriodsError(error.message));
  } finally {
    dispatch(setPeriodsLoading(false));
  }
};

// Fetch specific period status
export const fetchPeriodStatusThunk = (facilityId, year, month) => async (dispatch) => {
  try {
    dispatch(setPeriodsLoading(true));
    const period = await fetchPeriodStatus(facilityId, year, month);
    dispatch(setCurrentPeriod(period));
    return period;
  } catch (error) {
    console.error('Error fetching period status:', error);
    dispatch(setPeriodsError(error.message));
    throw error;
  } finally {
    dispatch(setPeriodsLoading(false));
  }
};

// Initiate soft close for a period
export const softClosePeriod = (facilityId, year, month) => async (dispatch) => {
  try {
    dispatch(setPeriodsLoading(true));
    const result = await apiSoftClose(facilityId, year, month);
    dispatch(updatePeriod(result.period));
    return result;
  } catch (error) {
    console.error('Error initiating soft close:', error);
    dispatch(setPeriodsError(error.message));
    throw error;
  } finally {
    dispatch(setPeriodsLoading(false));
  }
};

// Initiate hard close for a period
export const hardClosePeriod = (facilityId, year, month) => async (dispatch) => {
  try {
    dispatch(setPeriodsLoading(true));
    const result = await apiHardClose(facilityId, year, month);
    dispatch(updatePeriod(result.period));
    return result;
  } catch (error) {
    console.error('Error initiating hard close:', error);
    dispatch(setPeriodsError(error.message));
    throw error;
  } finally {
    dispatch(setPeriodsLoading(false));
  }
};

// Reopen a closed period
export const reopenBillingPeriod = (facilityId, year, month, reason) => async (dispatch) => {
  try {
    dispatch(setPeriodsLoading(true));
    const result = await apiReopenPeriod(facilityId, year, month, reason);
    dispatch(updatePeriod(result.period));
    return result;
  } catch (error) {
    console.error('Error reopening period:', error);
    dispatch(setPeriodsError(error.message));
    throw error;
  } finally {
    dispatch(setPeriodsLoading(false));
  }
};

// Validate a transaction date
export const validatePeriodForTransaction = (facilityId, transactionDate, transactionType = 'edit') => async (dispatch) => {
  try {
    const result = await apiValidateDate(facilityId, transactionDate, transactionType);
    dispatch(setPeriodValidation(result));
    return result;
  } catch (error) {
    console.error('Error validating transaction date:', error);
    // Return a default "allowed" response if validation fails
    const defaultResult = { allowed: true, status: 'open', message: 'Validation unavailable' };
    dispatch(setPeriodValidation(defaultResult));
    return defaultResult;
  }
};

// Clear period validation
export const clearValidation = () => (dispatch) => {
  dispatch(clearPeriodValidation());
};

// Refresh period summary
export const refreshPeriodSummaryThunk = (facilityId, year, month) => async (dispatch) => {
  try {
    dispatch(setPeriodsLoading(true));
    const result = await apiRefreshSummary(facilityId, year, month);
    // Fetch updated period to get new summary
    const period = await fetchPeriodStatus(facilityId, year, month);
    dispatch(updatePeriod(period));
    return result;
  } catch (error) {
    console.error('Error refreshing period summary:', error);
    dispatch(setPeriodsError(error.message));
    throw error;
  } finally {
    dispatch(setPeriodsLoading(false));
  }
};

// Fetch billing configuration
export const fetchBillingConfiguration = (facilityId) => async (dispatch) => {
  try {
    const config = await apiFetchConfig(facilityId);
    dispatch(setBillingConfig(config));
    return config;
  } catch (error) {
    console.error('Error fetching billing config:', error);
    throw error;
  }
};

// Update billing configuration
export const updateBillingConfiguration = (facilityId, config) => async (dispatch) => {
  try {
    const result = await apiUpdateConfig(facilityId, config);
    dispatch(setBillingConfig(result.config));
    return result;
  } catch (error) {
    console.error('Error updating billing config:', error);
    throw error;
  }
};